import React from 'react';
import ReactDOM from 'react-dom/client';
import { Form } from '@formio/react';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import FieldWorkerSVG from 'src/components/Reports_V2/FieldWorkerSVG';
import {
    PAGE,
    PX_PER_MM,
    LOGO_HEIGHT_MM,
    LOGO_MAX_WIDTH_MM,
    FOOTER_HEIGHT_MM,
    BRAND_MARK_HEIGHT_MM,
    MAX_GOAL_LINES,
    computeGeometry,
    computePageBreaks,
} from './formsPdfLayout';

/**
 * Renders a set of Form.io forms into a single PDF, one form per page-run, with the
 * company logo + task details repeated as a header on every page.
 *
 * Each form is rendered off-screen, rasterised with html2canvas, then sliced into
 * pages at component boundaries (see formsPdfLayout.js for the pagination rules).
 */

/**
 * Form.io layouts are Bootstrap-based and 768px is the `md` breakpoint — rendering
 * narrower silently collapses multi-column layouts into a single column.
 */
const RENDER_WIDTH_PX = 800;

/**
 * html2canvas allocates width x height x 4 bytes. Safari refuses canvases beyond
 * roughly 16.7M pixels and hands back a blank one with no error, so cap the capture
 * scale by total area rather than trusting a fixed multiplier. At 2x a full-width
 * form still lands at ~215 DPI once scaled onto the page.
 */
const MAX_CANVAS_PIXELS = 16000000;
const MAX_CAPTURE_SCALE = 2;

/** Never let one wedged form hang the whole export. */
const FORM_READY_TIMEOUT_MS = 15000;
const ASSET_TIMEOUT_MS = 3000;

/** How still the form's DOM has to be before it's safe to rasterise. */
const DOM_QUIET_MS = 250;
const DOM_QUIET_MAX_WAIT_MS = 5000;

const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/** Marks an error whose message is safe (and useful) to show the user directly. */
const userFacingError = (message) => Object.assign(new Error(message), { userMessage: message });

/**
 * Cancellation. html2canvas can't be interrupted mid-capture, so the signal is only
 * honoured between forms and at the few await points around one — long enough to keep
 * a fifty-form export responsive without leaving a half-drawn page behind.
 */
export const isAbortError = (error) => error?.name === 'AbortError';

const abortError = () => Object.assign(new Error('The export was cancelled.'), { name: 'AbortError' });

const throwIfAborted = (signal) => {
    if (signal?.aborted) throw abortError();
};

const withTimeout = (promise, ms, label) => Promise.race([
    Promise.resolve(promise),
    delay(ms).then(() => {
        console.warn(`Bulk form PDF: timed out waiting for ${label}`);
        return null;
    }),
]);

/* -------------------------------------------------------------------- logo */

/**
 * Fetches the company logo and normalises it to a PNG data URL with its aspect ratio.
 *
 * jsPDF only reliably embeds PNG and JPEG, while company logos arrive as anything
 * (webp, gif, svg); re-encoding through a canvas means a non-PNG logo no longer
 * throws inside addImage and vanish from the header.
 *
 * @returns {Promise<{dataUrl: string, widthMM: number, heightMM: number} | null>}
 */
const fetchCompanyLogo = async (url) => {
    try {
        const response = await fetch(url, { mode: 'cors' });
        if (!response.ok) return null;

        const blob = await response.blob();
        const sourceDataUrl = await new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result);
            reader.onerror = reject;
            reader.readAsDataURL(blob);
        });

        const image = new Image();
        image.src = sourceDataUrl;
        await image.decode();
        if (!image.naturalWidth || !image.naturalHeight) return null;

        const canvas = document.createElement('canvas');
        canvas.width = image.naturalWidth;
        canvas.height = image.naturalHeight;
        canvas.getContext('2d').drawImage(image, 0, 0);
        const dataUrl = canvas.toDataURL('image/png');
        canvas.width = 0;
        canvas.height = 0;

        // Preserve the aspect ratio instead of squashing every logo into a square.
        const aspect = image.naturalWidth / image.naturalHeight;
        const widthMM = Math.min(LOGO_HEIGHT_MM * aspect, LOGO_MAX_WIDTH_MM);
        const heightMM = widthMM / aspect;

        return { dataUrl, widthMM, heightMM };
    } catch (error) {
        console.warn('Could not load the company logo for the PDF header', error);
        return null;
    }
};

const logoCache = new Map();

/**
 * The logo is the same for every export in a session, so the fetch, decode and PNG
 * re-encode happen once per URL rather than once per click.
 */
export const loadCompanyLogo = (url) => {
    if (!url) return Promise.resolve(null);
    if (!logoCache.has(url)) logoCache.set(url, fetchCompanyLogo(url).catch(() => null));
    return logoCache.get(url);
};

/* ------------------------------------------------------------------ footer */

let brandMarkPromise = null;

/**
 * Rasterises the FieldWorker wordmark for the page footer.
 *
 * The mark is reused from the Reports_V2 component rather than duplicated here, so
 * it stays in step with the one the reports show. jsPDF can't place SVG directly, so
 * the rendered markup is serialised and drawn well above its printed size to stay
 * crisp. Resolved once and shared across every page of every export.
 *
 * @returns {Promise<{dataUrl: string, aspect: number} | null>}
 */
const rasteriseBrandMark = async () => {
    const host = document.createElement('div');
    host.style.cssText = 'position:absolute;left:-10000px;top:0';
    document.body.appendChild(host);

    const root = ReactDOM.createRoot(host);

    try {
        root.render(React.createElement(FieldWorkerSVG));
        await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));

        const svg = host.querySelector('svg');
        if (!svg) return null;

        const viewBox = (svg.getAttribute('viewBox') || '').split(/\s+/).map(Number);
        const aspect = viewBox.length === 4 && viewBox[3] ? viewBox[2] / viewBox[3] : 224 / 41;

        const height = Math.round(BRAND_MARK_HEIGHT_MM * PX_PER_MM * 6);
        const width = Math.round(height * aspect);

        const clone = svg.cloneNode(true);
        clone.setAttribute('width', String(width));
        clone.setAttribute('height', String(height));
        clone.setAttribute('xmlns', 'http://www.w3.org/2000/svg');

        const markup = new XMLSerializer().serializeToString(clone);
        const image = new Image();

        // onload rather than decode(): Safari's decode() is unreliable for SVG sources.
        await Promise.race([
            new Promise((resolve, reject) => {
                image.onload = resolve;
                image.onerror = () => reject(new Error('the mark could not be rasterised'));
                image.src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(markup)}`;
            }),
            delay(ASSET_TIMEOUT_MS),
        ]);
        if (!image.naturalWidth) return null;

        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        canvas.getContext('2d').drawImage(image, 0, 0, width, height);
        const dataUrl = canvas.toDataURL('image/png');
        canvas.width = 0;
        canvas.height = 0;

        return { dataUrl, aspect };
    } catch (error) {
        console.warn('Could not prepare the FieldWorker mark for the PDF footer', error);
        return null;
    } finally {
        try { root.unmount(); } catch (error) { /* already gone */ }
        if (host.parentNode) host.parentNode.removeChild(host);
    }
};

const loadBrandMark = () => {
    if (!brandMarkPromise) brandMarkPromise = rasteriseBrandMark().catch(() => null);
    return brandMarkPromise;
};

const FOOTER_LABEL = 'Generated using ';

const drawFooter = (pdf, brandMark) => {
    const baselineY = PAGE.height - PAGE.margin - 2;

    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(8);
    const labelWidth = pdf.getTextWidth(FOOTER_LABEL);

    pdf.setTextColor(120);
    // Without the mark the credit still has to read as a sentence.
    pdf.text(brandMark ? FOOTER_LABEL : `${FOOTER_LABEL}FieldWorker`, PAGE.margin, baselineY);
    pdf.setTextColor(0);

    if (brandMark) {
        try {
            pdf.addImage(
                brandMark.dataUrl, 'PNG',
                PAGE.margin + labelWidth, baselineY - BRAND_MARK_HEIGHT_MM + 0.8,
                BRAND_MARK_HEIGHT_MM * brandMark.aspect, BRAND_MARK_HEIGHT_MM,
            );
        } catch (error) {
            console.warn('Could not draw the FieldWorker mark in the PDF footer', error);
        }
    }
};

/* ------------------------------------------------------------------ header */

/**
 * Works out the wrapped header lines and where the header ends, without drawing.
 * The result is reused for every page of a form, and its measured height is what
 * the body layout is based on — so a long company or customer name pushes the form
 * down instead of running off the page or being drawn over.
 */
const layoutHeader = (pdf, header = {}) => {
    const logo = header.logo || null;
    const goals = (header.goals || []).filter(Boolean);
    const hasContent = logo || header.companyName || header.line2 || header.line3 || goals.length;
    if (!hasContent) return { lines: [], logo: null, bottomMM: PAGE.margin };

    const titleX = logo ? PAGE.margin + logo.widthMM + 4 : PAGE.margin;
    const lines = [];
    let baseline = PAGE.margin + 5;
    let lastBaseline = PAGE.margin;

    const push = (text, x, fontSize, fontStyle, lineHeight, maxLines) => {
        pdf.setFont('helvetica', fontStyle);
        pdf.setFontSize(fontSize);

        let wrapped = pdf.splitTextToSize(String(text), PAGE.width - x - PAGE.margin);
        if (maxLines && wrapped.length > maxLines) {
            wrapped = wrapped.slice(0, maxLines);
            wrapped[maxLines - 1] = `${wrapped[maxLines - 1].replace(/\s+\S*$/, '')}…`;
        }

        wrapped.forEach((line) => {
            lines.push({ text: line, x, y: baseline, fontSize, fontStyle });
            lastBaseline = baseline;
            baseline += lineHeight;
        });
    };

    if (header.companyName) push(header.companyName, titleX, 10, 'bold', 5);

    // Clear the logo so the detail lines can use the full page width.
    if (logo) baseline = Math.max(baseline, PAGE.margin + logo.heightMM + 4);

    if (header.line2) push(header.line2, PAGE.margin, 9, 'normal', 4.5);
    if (header.line3) push(header.line3, PAGE.margin, 9, 'normal', 4.5);

    if (goals.length) {
        const numbered = goals.map((goal, index) => `${index + 1}. ${goal}`).join('    ');
        push(`Goals: ${numbered}`, PAGE.margin, 9, 'normal', 4.5, MAX_GOAL_LINES);
    }

    const logoBottomMM = logo ? PAGE.margin + logo.heightMM : PAGE.margin;
    return { lines, logo, bottomMM: Math.max(lastBaseline + 2, logoBottomMM) };
};

const drawHeader = (pdf, layout) => {
    if (!layout) return;

    if (layout.logo) {
        try {
            pdf.addImage(
                layout.logo.dataUrl, 'PNG',
                PAGE.margin, PAGE.margin,
                layout.logo.widthMM, layout.logo.heightMM,
            );
        } catch (error) {
            // The logo is decorative; it must never take the export down with it.
            console.warn('Could not draw the logo in the PDF header', error);
        }
    }

    layout.lines.forEach((line) => {
        pdf.setFont('helvetica', line.fontStyle);
        pdf.setFontSize(line.fontSize);
        pdf.text(line.text, line.x, line.y);
    });
};

/* ------------------------------------------------------- rendering a form */

/**
 * Resolves once the subtree has stopped changing for `quietMs`.
 *
 * Form.io keeps mutating after the data lands — conditional components appear,
 * widgets attach lazily, calculated values redraw. html2canvas reads computed styles
 * as it walks, and a node replaced mid-walk is detached by the time it's read, at
 * which point every property comes back as an empty string and its CSS parser dies
 * on "unexpected EOF". Waiting for quiet is what stops that happening.
 */
const waitForQuietDom = (container, quietMs = DOM_QUIET_MS, maxWaitMs = DOM_QUIET_MAX_WAIT_MS) => (
    new Promise((resolve) => {
        let quietTimer = null;

        const finish = () => {
            clearTimeout(quietTimer);
            clearTimeout(hardStop);
            observer.disconnect();
            resolve();
        };

        const observer = new MutationObserver(() => {
            clearTimeout(quietTimer);
            quietTimer = setTimeout(finish, quietMs);
        });

        const hardStop = setTimeout(finish, maxWaitMs);

        observer.observe(container, { childList: true, subtree: true, attributes: true, characterData: true });
        quietTimer = setTimeout(finish, quietMs);
    })
);

/** Waits for fonts, images and a still DOM so html2canvas gets a stable tree. */
const waitForPaint = async (container) => {
    if (document.fonts && document.fonts.ready) {
        await Promise.race([document.fonts.ready, delay(ASSET_TIMEOUT_MS)]);
    }

    const images = Array.from(container.querySelectorAll('img'));
    await Promise.all(images.map((image) => (
        image.complete && image.naturalWidth
            ? Promise.resolve()
            : Promise.race([image.decode().catch(() => null), delay(ASSET_TIMEOUT_MS)])
    )));

    await waitForQuietDom(container);

    // Two frames: one to lay out, one to paint.
    await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));
};

/**
 * Initial values for every property html2canvas parses as a single value.
 *
 * html2canvas 1.4.1 reads these off getComputedStyle and feeds each straight into its
 * CSS parser. `parse()` falls back to the property's initial value for null/undefined
 * but *not* for an empty string, and its parser throws on empty input — the
 * "Error parsing CSS component value, unexpected EOF" crash. Browsers do hand back ''
 * for properties they can't resolve on some elements, so the only lever from outside
 * the library is to give those elements an explicit value before the tree is parsed.
 *
 * Properties parsed as lists (content, quotes, transitions, …) tolerate empty input,
 * so they're deliberately absent here.
 */
const SINGLE_VALUE_CSS_FALLBACKS = {
    direction: 'ltr',
    float: 'none',
    fontStyle: 'normal',
    fontWeight: '400',
    letterSpacing: 'normal',
    lineBreak: 'auto',
    lineHeight: 'normal',
    listStyleImage: 'none',
    listStylePosition: 'outside',
    listStyleType: 'disc',
    opacity: '1',
    overflowWrap: 'normal',
    position: 'static',
    textAlign: 'start',
    textTransform: 'none',
    transform: 'none',
    visibility: 'visible',
    wordBreak: 'normal',
    zIndex: 'auto',
};

const CSS_FALLBACK_ENTRIES = Object.entries(SINGLE_VALUE_CSS_FALLBACKS);

let reportedEmptyStyles = false;

const describeElement = (element) => {
    const classes = typeof element.className === 'string' ? element.className.trim() : '';
    return `${element.nodeName.toLowerCase()}${classes ? `.${classes.split(/\s+/).join('.')}` : ''}`;
};

/**
 * Gives an explicit value to any property that reads back empty, so html2canvas's
 * parser never sees one. Runs against the cloned document, using the same
 * `window.getComputedStyle` call html2canvas itself makes, so what it checks is
 * exactly what the library is about to read.
 */
const repairEmptyComputedStyles = (root) => {
    if (!root || !root.querySelectorAll) return;

    const repaired = [];
    const dropped = [];

    [root, ...root.querySelectorAll('*')].forEach((element) => {
        // Children of an element removed below are still in this list but detached,
        // and everything reads empty on a detached node.
        if (!element.style || !element.isConnected) return;

        const computed = window.getComputedStyle(element, null);
        // A property the browser doesn't implement reads back as undefined, which
        // html2canvas already falls back on. Only '' is dangerous.
        const empty = CSS_FALLBACK_ENTRIES.filter(([property]) => computed[property] === '');
        if (!empty.length) return;

        empty.forEach(([property, fallback]) => {
            element.style[property] = fallback;
            repaired.push(`${describeElement(element)} { ${property} }`);
        });

        // If it still won't resolve, this element is one html2canvas cannot parse at
        // all. Dropping it from the clone costs that element's pixels; leaving it in
        // costs the entire PDF.
        const recheck = window.getComputedStyle(element, null);
        if (empty.some(([property]) => recheck[property] === '') && element !== root && element.parentNode) {
            dropped.push(describeElement(element));
            element.parentNode.removeChild(element);
        }
    });

    if (repaired.length && !reportedEmptyStyles) {
        reportedEmptyStyles = true;
        console.warn(
            `Bulk form PDF: gave ${repaired.length} empty computed style value(s) an explicit `
            + 'fallback so html2canvas could parse them.',
            repaired.slice(0, 25),
        );
    }

    if (dropped.length) {
        console.warn(
            `Bulk form PDF: ${dropped.length} element(s) could not be made parseable and were `
            + 'left out of the capture.',
            dropped.slice(0, 25),
        );
    }
};

const captureScale = (container) => {
    const width = container.scrollWidth || RENDER_WIDTH_PX;
    const height = container.scrollHeight || 1;
    const byArea = Math.sqrt(MAX_CANVAS_PIXELS / (width * height));
    return Math.max(1, Math.min(MAX_CAPTURE_SCALE, byArea));
};

/**
 * Offsets (in CSS px, relative to the container) where cutting the form is tidy —
 * after a component or group, and before a heading or label.
 */
const findSafeBreaks = (container) => {
    const selector = [
        '.row', '[class*="col-"]',
        '.formio-form > .formio-component', '.formio-component',
        '.formio-panel', '.formio-fieldset', '.form-group', '.card',
        'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
        'label', '.control-label', '.formio-label',
    ].join(',');

    const rootRect = container.getBoundingClientRect();
    const fullHeight = Math.max(container.scrollHeight, container.clientHeight);
    const breaks = [fullHeight];

    container.querySelectorAll(selector).forEach((element) => {
        const rect = element.getBoundingClientRect();
        breaks.push(Math.round(Math.max(0, rect.bottom - rootRect.top) + 4));

        const isHeading = /^H[1-6]$/i.test(element.tagName)
            || element.classList.contains('control-label')
            || element.classList.contains('formio-label');
        if (isHeading) {
            breaks.push(Math.max(0, Math.round(Math.max(0, rect.top - rootRect.top) - 6)));
        }
    });

    return Array.from(new Set(breaks))
        .filter((y) => y > 20 && y < fullHeight)
        .sort((a, b) => a - b);
};

/**
 * Blocks (in CSS px) that should not be cut through — a label and its control, an
 * input, a nested container component. Returned raw and unmerged: whether a block is
 * small enough to be worth protecting depends on the page height, which isn't known
 * until the form has been captured.
 */
const findAtomicBlocks = (container) => {
    const selector = [
        '.formio-component:not(.formio-panel):not(.formio-fieldset)',
        '.form-group',
        'input', 'textarea', 'select', '.form-control',
        '.avoid-break', // manual opt-in via a custom class on a Form.io component
    ].join(',');

    const rootRect = container.getBoundingClientRect();

    return Array.from(container.querySelectorAll(selector))
        .map((element) => {
            const rect = element.getBoundingClientRect();
            return [
                Math.round(Math.max(0, rect.top - rootRect.top)),
                Math.round(Math.max(0, rect.bottom - rootRect.top)),
            ];
        })
        .filter(([top, bottom]) => bottom > top);
};

/**
 * Last row of the canvas holding anything visible.
 *
 * The container's padding and Form.io's trailing margins leave a strip of white at
 * the bottom of every capture. Paginating over it produces a final page containing
 * nothing — which is what the old `deletePage` calls were there to remove, at the
 * cost of deleting real content whenever the strip wasn't there.
 *
 * Read a band at a time rather than a row at a time: `getImageData` is the expensive
 * part (it allocates and copies out of the backing store), and a tall form used to
 * cost well over a thousand of those calls. Scanning within the band is a walk over a
 * typed array, so the whole search normally ends in the first band — the trailing
 * whitespace is only ever a strip.
 */
const CONTENT_SCAN_BAND_PX = 64;

const findContentBottom = (canvas) => {
    try {
        const context = canvas.getContext('2d', { willReadFrequently: true });
        const rowLength = canvas.width * 4;

        for (let bandBottom = canvas.height; bandBottom > 0; bandBottom -= CONTENT_SCAN_BAND_PX) {
            const bandTop = Math.max(0, bandBottom - CONTENT_SCAN_BAND_PX);
            const { data } = context.getImageData(0, bandTop, canvas.width, bandBottom - bandTop);

            // Bottom-up, so the first row with ink is the lowest one in the band.
            for (let row = bandBottom - bandTop - 1; row >= 0; row--) {
                const start = row * rowLength;
                for (let i = start; i < start + rowLength; i += 4) {
                    const isVisible = data[i + 3] > 8;
                    const isInk = data[i] < 250 || data[i + 1] < 250 || data[i + 2] < 250;
                    // A couple of rows of slack so antialiasing on the last glyph survives.
                    if (isVisible && isInk) return Math.min(canvas.height, bandTop + row + 2);
                }
            }
        }
        return 0;
    } catch (error) {
        // A tainted canvas can't be sampled; fall back to using the whole thing.
        console.warn('Could not measure the form content height', error);
        return canvas.height;
    }
};

/**
 * Mounts one form off-screen, waits for it to be genuinely ready, and rasterises it.
 * @returns {Promise<{canvas: HTMLCanvasElement, contentHeight: number, cssWidth: number,
 *                    safeBreaks: number[], atomicBlocks: number[][]} | null>}
 */
const renderFormToCanvas = async (formDoc, signal) => {
    const container = document.createElement('div');
    container.style.cssText = [
        'position:absolute',
        'left:-10000px',
        'top:0',
        `width:${RENDER_WIDTH_PX}px`,
        'padding:24px',
        'background:#ffffff',
        'box-sizing:border-box',
    ].join(';');
    document.body.appendChild(container);

    const root = ReactDOM.createRoot(container);

    try {
        // `formReady` fires once the form instance exists — but *before* the submission
        // has been applied, which is what the old fixed 500ms sleep was covering for.
        // The submission prop is still passed as a fallback; setSubmission resolves
        // only after the values are actually in the DOM.
        const instance = await withTimeout(
            new Promise((resolve) => {
                root.render(React.createElement(Form, {
                    form: formDoc.form,
                    // The submission is deliberately NOT passed as a prop: @formio/react
                    // re-applies it from an effect once its own state settles, and that
                    // second Form.io redraw can land while html2canvas is walking the
                    // tree — handing it detached nodes whose computed styles are all
                    // empty ("Error parsing CSS component value, unexpected EOF").
                    // Applying it once, below, keeps the DOM still.
                    options: {
                        readOnly: true,
                        // Without this a wizard renders (and therefore prints) page one only.
                        flatten: true,
                        noAlerts: true,
                    },
                    formReady: resolve,
                }));
            }),
            FORM_READY_TIMEOUT_MS,
            'the form to render',
        );

        if (instance && formDoc.submission && typeof instance.setSubmission === 'function') {
            const applied = (async () => instance.setSubmission(formDoc.submission))()
                .catch((error) => console.warn('Could not apply the form data for the PDF', error));
            await withTimeout(applied, FORM_READY_TIMEOUT_MS, 'the form data to be applied');
        }

        await waitForPaint(container);
        // Settling a form is the slow half; bail here rather than pay for a capture
        // whose pages are about to be thrown away.
        throwIfAborted(signal);

        // Measure against the live DOM before the capture — cheap, and the container
        // is about to be torn down.
        const cssWidth = container.scrollWidth;
        const safeBreaks = findSafeBreaks(container);
        const atomicBlocks = findAtomicBlocks(container);

        const capture = () => html2canvas(container, {
            scale: captureScale(container),
            logging: false,
            useCORS: true,
            // Tainting the canvas makes every later toDataURL throw, which would fail
            // the whole export; dropping the offending image is far better.
            allowTaint: false,
            backgroundColor: '#ffffff',
            windowWidth: container.scrollWidth,
            windowHeight: container.scrollHeight,
            // Runs after the clone lands in html2canvas's iframe and before it parses
            // the tree — the one point where an unparseable computed value can still
            // be corrected.
            onclone: (_clonedDocument, clonedElement) => {
                try {
                    repairEmptyComputedStyles(clonedElement);
                } catch (error) {
                    console.warn('Could not pre-check computed styles for the capture', error);
                }
            },
        });

        let canvas;
        try {
            canvas = await capture();
        } catch (error) {
            // This class of failure is a race against a late redraw, so a second pass
            // after the tree has settled usually succeeds.
            console.warn('Form capture failed, settling and retrying once', error);
            await waitForQuietDom(container, DOM_QUIET_MS * 2, DOM_QUIET_MAX_WAIT_MS);
            canvas = await capture();
        }

        return { canvas, contentHeight: findContentBottom(canvas), cssWidth, safeBreaks, atomicBlocks };
    } finally {
        try { root.unmount(); } catch (error) { /* already gone */ }
        if (container.parentNode) container.parentNode.removeChild(container);
    }
};

/** Draws one slice of the form canvas onto the current PDF page. */
const drawSlice = (pdf, canvas, top, bottom, geometry) => {
    const height = Math.max(1, Math.min(bottom - top, canvas.height - top));

    const slice = document.createElement('canvas');
    slice.width = canvas.width;
    slice.height = height;
    slice.getContext('2d').drawImage(canvas, 0, top, canvas.width, height, 0, 0, canvas.width, height);

    const imageData = slice.toDataURL('image/png', 1.0);
    slice.width = 0;
    slice.height = 0; // release now; Safari holds on to these across a long export

    pdf.addImage(
        imageData, 'PNG',
        PAGE.margin, geometry.contentTopMM,
        (canvas.width / PX_PER_MM) * geometry.scale,
        (height / PX_PER_MM) * geometry.scale,
        undefined, 'FAST',
    );
};

/* ----------------------------------------------------------------- public */

/**
 * A form is only dropped or cut short for one of these reasons, and every one of them
 * is reported back to the caller — a bulk export that quietly loses a form is worse
 * than one that says which forms it lost.
 */
const SKIP_REASONS = {
    render: 'could not be rendered',
    empty: 'came out blank',
    truncated: 'was too long and was cut short',
};

/**
 * @param {object}   args
 * @param {Array}    args.documents - `[{ form, submission, header }]`, one per form.
 *                                    `header` is `{ logo, companyName, line2, line3 }`.
 * @param {Function} [args.onProgress] - called as `(completed, total)`.
 * @param {AbortSignal} [args.signal] - cancels the export between forms.
 * @returns {Promise<{pdf: jsPDF, skipped: Array<{label: string, reason: string}>}>}
 *          the document (the caller decides whether to save or print it) plus anything
 *          that did not make it in whole.
 */
export const generateFormsPdf = async ({ documents, onProgress, signal }) => {
    if (!documents || !documents.length) throw userFacingError('No forms were selected.');

    const pdf = new jsPDF('p', 'mm', 'a4');
    const brandMark = await loadBrandMark();
    const skipped = [];
    let isFirstPage = true;

    for (let index = 0; index < documents.length; index++) {
        throwIfAborted(signal);
        if (onProgress) onProgress(index, documents.length);

        const formDoc = documents[index];
        const label = formDoc.label || `Form ${index + 1}`;
        const headerLayout = layoutHeader(pdf, formDoc.header);

        let rendered;
        try {
            // eslint-disable-next-line no-await-in-loop
            rendered = await renderFormToCanvas(formDoc, signal);
        } catch (error) {
            if (isAbortError(error)) throw error;
            // One unrenderable form must not cost the other forty-nine their export.
            console.error(`Could not render "${label}" for the bulk PDF`, error);
            skipped.push({ label, reason: SKIP_REASONS.render });
            continue;
        }

        const { canvas, contentHeight, cssWidth, safeBreaks, atomicBlocks } = rendered;

        try {
            const geometry = computeGeometry(canvas.width, headerLayout.bottomMM, FOOTER_HEIGHT_MM);
            const deviceScale = canvas.width / (cssWidth || RENDER_WIDTH_PX);
            const toCanvasPx = (y) => Math.round(y * deviceScale);

            const slices = computePageBreaks({
                contentHeight,
                pageHeightPx: geometry.pageHeightPx,
                safeBreaks: safeBreaks.map(toCanvasPx),
                avoidRanges: atomicBlocks.map(([top, bottom]) => [toCanvasPx(top), toCanvasPx(bottom)]),
            });

            // No slices means the capture held no ink — a form that never finished
            // rendering looks exactly like this, and used to disappear without a word.
            if (!slices.length) {
                skipped.push({ label, reason: SKIP_REASONS.empty });
                continue;
            }

            // Pagination gives up at MAX_PAGES_PER_FORM; say so rather than let the
            // tail of a runaway form go missing unannounced.
            if (slices[slices.length - 1][1] < contentHeight) {
                skipped.push({ label, reason: SKIP_REASONS.truncated });
            }

            for (const [top, bottom] of slices) {
                // Every page after the very first one is added here, and nothing is ever
                // deleted afterwards — a page exists only because there is content for it.
                if (isFirstPage) isFirstPage = false;
                else pdf.addPage();

                drawHeader(pdf, headerLayout);
                drawSlice(pdf, canvas, top, bottom, geometry);
                drawFooter(pdf, brandMark);
            }
        } finally {
            canvas.width = 0;
            canvas.height = 0;
        }
    }

    if (isFirstPage) throw userFacingError('None of the selected forms produced any printable content.');

    if (onProgress) onProgress(documents.length, documents.length);
    return { pdf, skipped };
};
